<script setup>
import axios from 'axios'
import { onMounted, reactive, ref } from 'vue'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import isBetween from 'dayjs/plugin/isBetween'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import Swal from 'sweetalert2'
import { labelCss } from '@/helpers/monthly_actual_report_status'
import Textarea from 'primevue/textarea'
import Dialog from 'primevue/dialog'

// eslint-disable-next-line no-unused-vars
const toast = useToast()

dayjs.extend(isSameOrBefore)
dayjs.extend(isToday)
dayjs.extend(isBetween)
dayjs.extend(utc)
dayjs.extend(duration)
const props = defineProps({
  approvable: {
    type: String,
    required: true
  },
  actualPeriod: {
    type: Object,
    required: true
  },
  monthlyActualReport: {
    type: Object,
    required: true
  }
})
const data = reactive({
  isLoading: false,
  actualDetails: [],
  creatorOptions: [],
  totalPrices: {},
  pdfUrl: ''
})
const layoutData = reactive({
  isMultiCreators: false,
  displayRejectionDialog: false,
  displayMessageDialog: false,
  isDisplayTotal: true,
  displayInvoiceModal: false,
  shouldSendInvoice: false
})
const rejectionMessage = ref('')
const selectedCreator = ref({
  id: 0,
  name: '',
  grandit_cd: '',
  includes_rest: false,
  tax_included: false
})
const timeDropdownList = []
for (let hour = 0; hour <= 23; hour++) {
  ;['00', '15', '30', '45'].forEach((i) => {
    timeDropdownList.push(`${hour.toString().padStart(2, '0')}:${i}`)
  })
}
onMounted(async () => {
  data.isLoading = true
  await fetchActualDetails()
  data.isLoading = false
})
const switchDisplayTotal = () => {
  layoutData.isDisplayTotal = !layoutData.isDisplayTotal
}
const isDisplayedScheduledTilde = (slotProps) => {
  return (
    slotProps.data.modified_actual_details.scheduled_time.start !== null ||
    slotProps.data.modified_actual_details.scheduled_time.end !== null
  )
}
const formatWorkDate = (detail) => {
  let workDate = dayjs(detail.work_date)
  let startTime = null
  if (detail.scheduled_time_over24?.start !== null)
    startTime = detail.scheduled_time_over24.start
  else if (detail.actual_time?.start !== null)
    startTime = detail.actual_time.start
  if (startTime !== null && Number(startTime.substr(0, 2)) < 8)
    workDate = workDate.subtract(1, 'd')
  return workDate
}
const switchCreator = async () => {
  await fetchActualDetails(selectedCreator.value.id)
}
const fetchActualDetails = async (creatorId = '') => {
  const result = await axios.get(
    `${location.pathname}.json?creator_id=${creatorId}`
  )
  data.actualDetails = result.data.actual_details.filter(
    (detail) => detail.disabled_at === null
  )
  addGroupKeys()
  data.creatorOptions = result.data.creator_options
  data.totalPrices = result.data.total_prices
  layoutData.isMultiCreators = result.data.company_id !== null
  layoutData.shouldSendInvoice = result.data.should_send_invoice
  selectedCreator.value = result.data.creator
}

// actualDetails取得時用 結合表示用のデータを追加します
const addGroupKeys = () => {
  data.actualDetails.forEach((detail) => {
    if (detail.grouped_actual_detail) {
      detail.group_key_checkbox = detail.grouped_actual_detail.id
      detail.group_key_tax = detail.grouped_actual_detail.id
      detail.group_key_price = detail.grouped_actual_detail.id
    } else {
      detail.group_key_checkbox = `A${detail.id}`
      detail.group_key_tax = `A${detail.id}`
      detail.group_key_price = `A${detail.id}`
    }
  })
}
const swalMixin = () => {
  return Swal.mixin({
    customClass: {
      confirmButton:
        'group flex items-center justify-center rounded-md border bg-green-400 py-2 pr-4 pl-3 text-sm font-medium text-white shadow-sm border-transparent hover:bg-green-500 sm:w-auto',
      cancelButton:
        'group mr-4 flex items-center justify-center rounded-md border border-transparent bg-gray-600 py-2 pl-3 pr-4 text-sm font-medium text-white shadow-sm sm:w-auto hover:bg-gray-800'
    },
    buttonsStyling: false
  })
}
const approve = async () => {
  const modal = await swalMixin().fire({
    title: '',
    text: '作業明細を承認してよろしいですか？',
    confirmButtonText: 'はい',
    confirmButtonColor: '#40BF6C',
    cancelButtonText: 'キャンセル',
    showCancelButton: true,
    reverseButtons: true,
    focusCancel: true
  })
  if (modal.isConfirmed) {
    data.isLoading = true
    try {
      await axios.put(
        `/monthly_actual_reports/${props.monthlyActualReport.id}/approval`
      )
      data.isLoading = false
      await Swal.fire({
        icon: 'success',
        title: '',
        text: '承認ありがとうございます。請求書の発行をお願いいたします。',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
      location.reload()
    } catch (error) {
      data.isLoading = false
      await Swal.fire({
        icon: 'error',
        title: '作業明細の承認に失敗しました',
        text: 'もう一度お試しください',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    }
  }
}
const reject = async () => {
  data.isLoading = true
  try {
    await axios.put(
      `/monthly_actual_reports/${props.monthlyActualReport.id}/reject`,
      {
        rejection_message: rejectionMessage.value
      }
    )
    layoutData.displayRejectionDialog = false
    data.isLoading = false
    await Swal.fire({
      icon: 'success',
      title: '',
      text: '作業明細を否認しました。',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
    location.reload()
  } catch (error) {
    data.isLoading = false
    await Swal.fire({
      icon: 'error',
      title: '作業明細の否認に失敗しました',
      text: 'もう一度お試しください',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
  }
}
const createPdf = async () => {
  data.isLoading = true
  try {
    const result = await axios.post(
      `/monthly_actual_reports/${props.monthlyActualReport.id}/invoice`
    )
    data.pdfUrl = result.data.url
    data.isLoading = false
    layoutData.displayInvoiceModal = true
  } catch (error) {
    data.isLoading = false
    await Swal.fire({
      icon: 'error',
      title: '請求書の作成に失敗しました',
      text: 'もう一度お試しください',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
    location.reload()
  }
}
const send_invoice = async () => {
  data.isLoading = true
  try {
    await axios.post(
      `/monthly_actual_reports/${props.monthlyActualReport.id}/send_invoice`
    )
    data.isLoading = false
    layoutData.displayInvoiceModal = false
    await Swal.fire({
      icon: 'success',
      title: '',
      text: '承認ありがとうございます。請求書を送付しました。',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
    location.reload()
  } catch (error) {
    data.isLoading = false
    layoutData.displayInvoiceModal = false
    await Swal.fire({
      icon: 'error',
      title: '作業明細の承認と請求書の送付に失敗しました',
      text: 'もう一度お試しください',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
    location.reload()
  }
}
</script>

<template>
  <vue-element-loading :active="data.isLoading" is-full-screen />
  <Toast
    :pt="{
      detail: { class: 'hidden' },
      icon: { class: 'w-6 h-6' },
      closeButton: { class: 'hidden' }
    }"
  />
  <div class="mb-3 flex justify-between">
    <div class="mt-3 flex-none sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <div class="flex-none sm:flex sm:items-center">
          <h1 class="flex items-center text-2xl font-semibold text-gray-800">
            {{ props.actualPeriod.month }}
            {{ props.monthlyActualReport.name }}様 作業明細
            <span
              class="label ml-3 inline-block rounded px-2 py-1 text-xs font-semibold"
              :class="labelCss(props.monthlyActualReport.status).class"
              >{{ labelCss(props.monthlyActualReport.status).name }}</span
            >
            <button
              v-if="props.monthlyActualReport.rejection_message !== null"
              class="group my-2.5 ml-3 flex items-center whitespace-nowrap rounded border border-red-500 bg-white py-1 pl-2.5 pr-3 text-xs font-thin text-red-500 shadow-sm hover:border-transparent hover:bg-red-400 hover:text-white sm:w-auto"
              @click="layoutData.displayMessageDialog = true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="mr-2 h-4 w-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
              </svg>
              <span class="font-semibold">否認メッセージ表示</span>
            </button>
          </h1>
          <Dialog
            v-model:visible="layoutData.displayMessageDialog"
            header="否認メッセージ"
            :modal="true"
            class="w-1/2"
          >
            <div class="whitespace-pre-line rounded-md border p-5">
              {{ props.monthlyActualReport.rejection_message }}
            </div>
            <template #footer>
              <div class="mt-4 flex flex-wrap items-center justify-end">
                <button
                  class="group my-2.5 mr-2 flex items-center whitespace-nowrap rounded-md border border-gray-500 bg-white py-1 pl-2.5 pr-3 text-sm font-medium shadow-sm hover:border-transparent hover:bg-gray-500 hover:text-white sm:w-auto"
                  @click="layoutData.displayMessageDialog = false"
                >
                  <span class="pl-1 text-gray-800 group-hover:text-white"
                    >閉じる</span
                  >
                </button>
              </div>
            </template>
          </Dialog>
        </div>
        <div class="mt-2 flex flex-row">
          <div class="flex items-center text-sm text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="mr-1 h-4 w-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
              />
            </svg>
            {{ props.actualPeriod.department }}
          </div>
          <div class="ml-4 flex items-center text-sm text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="mr-1 h-5 w-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
              />
            </svg>
            <a
              :href="`/creators/${selectedCreator.id}/schedules`"
              target="_blank"
              class="hover:text-gray-300 hover:underline"
              >月間スケジュール</a
            >
          </div>
        </div>
        <div class="flex flex-row items-end">
          <select
            v-if="layoutData.isMultiCreators"
            v-model="selectedCreator"
            class="h-8 items-center rounded-md border border-gray-300 bg-white px-3.5 font-medium text-gray-800 shadow-sm hover:bg-gray-50 hover:text-gray-800"
            @change="switchCreator"
          >
            <option
              v-for="creator in data.creatorOptions"
              v-show="creator.name"
              :key="creator.id"
              :value="{
                id: creator.id,
                name: creator.name,
                grandit_cd: creator.grandit_cd,
                includes_rest: creator.includes_rest,
                tax_included: creator.tax_included
              }"
            >
              {{ creator.name }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div class="flex flex-row items-end justify-between">
      <div class="flex flex-row">
        <button
          class="rounded-md border px-4 py-1.5 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
          :class="
            props.approvable === 'true'
              ? 'border-gray-800 bg-white text-gray-800 hover:border-transparent hover:bg-red-300 hover:text-white'
              : 'border-gray-400 bg-gray-100 text-gray-400'
          "
          :disabled="props.approvable === 'false'"
          @click="layoutData.displayRejectionDialog = true"
        >
          <div class="flex gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="h-5 w-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <div>承認しない</div>
          </div>
        </button>
        <Dialog
          v-model:visible="layoutData.displayRejectionDialog"
          header="作業明細を否認しますか？"
          :modal="true"
          class="w-1/2"
        >
          <div>否認理由をご記入ください。</div>
          <div class="text-sm">
            再度、注文書・作業明細書をお送りいたします。
          </div>
          <Textarea
            v-model="rejectionMessage"
            auto-resize
            class="focus:outline-blue string mt-2 h-10 w-full rounded border p-3 leading-normal"
          />
          <template #footer>
            <div class="mt-4 flex flex-wrap items-center justify-end">
              <button
                class="group my-2.5 mr-2 w-auto items-center whitespace-nowrap rounded-md border border-transparent bg-gray-600 py-2 pl-2.5 pr-3 font-medium text-white text-white shadow-sm hover:bg-gray-800"
                @click="layoutData.displayRejectionDialog = false"
              >
                <span class="pl-1">キャンセル</span>
              </button>
              <button
                :class="[
                  rejectionMessage === '' ? 'disabled:opacity-25' : '',
                  'group my-2.5 mr-2 flex w-auto items-center whitespace-nowrap rounded-md border-transparent bg-red-300 py-2 pl-2.5 pr-3 font-medium text-white shadow-sm'
                ]"
                :disabled="rejectionMessage === ''"
                @click="reject"
              >
                <span
                  class="pl-1 text-gray-800"
                  :class="[
                    rejectionMessage === '' ? '' : 'group-hover:text-white'
                  ]"
                  >送信</span
                >
              </button>
            </div>
          </template>
        </Dialog>
        <button
          v-if="layoutData.shouldSendInvoice"
          class="ml-4 rounded-md border px-4 py-1.5 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
          :class="
            props.approvable === 'true'
              ? 'border-gray-800 bg-white text-gray-800 hover:border-transparent hover:bg-green-400 hover:text-white'
              : 'border-gray-400 bg-gray-100 text-gray-400'
          "
          :disabled="props.approvable === 'false'"
          @click="createPdf"
        >
          <div class="flex gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="h-5 w-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <div>承認して請求書を送付</div>
          </div>
        </button>
        <button
          v-else
          class="ml-4 rounded-md border px-4 py-1.5 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
          :class="
            props.approvable === 'true'
              ? 'border-gray-800 bg-white text-gray-800 hover:border-transparent hover:bg-green-400 hover:text-white'
              : 'border-gray-400 bg-gray-100 text-gray-400'
          "
          :disabled="props.approvable === 'false'"
          @click="approve"
        >
          <div class="flex gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="h-5 w-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <div>承認する</div>
          </div>
        </button>
        <Dialog
          v-model:visible="layoutData.displayInvoiceModal"
          :header="`${props.monthlyActualReport.name} 様 請求書`"
          :modal="true"
          :style="{ width: '80vw' }"
        >
          <div>請求書を送付します。内容をご確認ください。</div>
          <div class="text-sm">
            不備がある場合はキャンセルして、「承認しない」ボタンから作業明細を否認してください。
          </div>
          <div class="mt-2 h-[60vh]">
            <object
              :data="data.pdfUrl"
              type="application/pdf"
              class="h-full w-full flex-1 border"
            />
          </div>
          <template #footer>
            <div class="mt-4 flex items-center justify-between">
              <div>
                <button
                  class="group my-2.5 mr-2 w-auto items-center whitespace-nowrap rounded-md border border-transparent bg-gray-600 py-2 pl-2.5 pr-3 font-medium text-white shadow-sm hover:bg-gray-800"
                  @click="layoutData.displayInvoiceModal = false"
                >
                  <span class="pl-1">キャンセル</span>
                </button>
              </div>
              <div>
                <button
                  class="group my-2.5 mr-2 w-auto items-center whitespace-nowrap rounded-md border border-transparent bg-green-400 py-2 pl-2.5 pr-3 font-medium text-white shadow-sm hover:bg-green-500"
                  @click="send_invoice"
                >
                  <span class="pl-1">承認して請求書を送付</span>
                </button>
              </div>
            </div>
          </template>
          <vue-element-loading :active="isLoading" />
        </Dialog>
      </div>
    </div>
  </div>
  <div class="mx-2">
    <input
      id="displayTotal"
      type="checkbox"
      class="text-indigo-600 focus:ring-indigo-500 mr-1 mt-4 h-3 w-3 rounded border-gray-300 underline drop-shadow"
      :checked="layoutData.isDisplayTotal"
      @change="switchDisplayTotal"
    />
    <label for="displayTotal" class="text-sm text-gray-800">合計を表示</label>
  </div>
  <div class="card overflow-auto">
    <DataTable
      :value="data.actualDetails"
      scroll-height="flex"
      class="z-50"
      row-group-mode="rowspan"
      :group-rows-by="[
        'group_key_checkbox',
        'group_key_tax',
        'group_key_price'
      ]"
      size="small"
      scrollable
      maximizable
      show-gridlines
      striped-rows
      row-hover
    >
      <Column field="workDate" class="px-2 text-center text-sm">
        <template #header>
          <span class="flex-1 py-1 text-center">作業日</span>
        </template>
        <template #body="slotProps">
          <div class="inline-flex items-center">
            <span class="text-sm text-gray-800">
              {{
                formatWorkDate(slotProps.data.modified_actual_details).format(
                  'M/D'
                )
              }}
            </span>
          </div>
        </template>
      </Column>
      <Column field="title" header="タイトル" class="px-2 text-sm">
        <template #body="slotProps">
          <div class="inline-flex items-center text-sm">
            <span class="text-sm text-gray-800">
              {{ slotProps.data.modified_actual_details.title }}
            </span>
          </div>
        </template>
      </Column>
      <Column field="scheduledStartTime" class="px-2 text-center text-sm">
        <template #header>
          <span class="flex-1 text-center">予定開始時刻</span>
        </template>
        <template #body="slotProps">
          <div class="inline-flex items-center text-sm">
            <span class="text-sm text-gray-800">
              {{
                slotProps.data.modified_actual_details.scheduled_time_over24
                  .start
              }}
            </span>
          </div>
        </template>
      </Column>
      <Column field="scheduledTilde" class="text-sm">
        <template #body="slotProps">
          <span
            v-if="isDisplayedScheduledTilde(slotProps)"
            class="inline-flex items-center text-sm text-gray-800"
          >
            〜
          </span>
        </template>
      </Column>
      <Column field="scheduledEndTime" class="px-2 text-center text-sm">
        <template #header>
          <span class="flex-1 text-center">予定終了時刻</span>
        </template>
        <template #body="slotProps">
          <div class="inline-flex items-center text-sm">
            <span class="text-sm text-gray-800">
              {{
                slotProps.data.modified_actual_details.scheduled_time_over24.end
              }}
            </span>
          </div>
        </template>
      </Column>
      <Column field="startTime" class="px-2 text-center text-sm">
        <template #header>
          <span class="flex-1 text-center">実績開始時刻</span>
        </template>
        <template #body="slotProps">
          <span class="inline-flex items-center text-sm text-gray-800">
            {{ slotProps.data.actual_time.start }}
          </span>
        </template>
      </Column>
      <Column field="actualTilde" class="text-sm">
        <template #body="slotProps">
          <span
            v-if="
              slotProps.data.actual_time.start !== null ||
              slotProps.data.actual_time.end !== null
            "
            class="inline-flex items-center text-sm text-gray-800"
          >
            〜
          </span>
        </template>
      </Column>
      <Column field="endTime" class="px-2 text-center text-sm">
        <template #header>
          <span class="flex-1 text-center">実績終了時刻</span>
        </template>
        <template #body="slotProps">
          <span class="inline-flex items-center text-sm text-gray-800">
            {{ slotProps.data.actual_time.end }}
          </span>
        </template>
      </Column>
      <Column field="includesRest" class="px-2 text-center text-sm">
        <template #header>
          <span class="flex-1 text-center">休憩時間</span>
        </template>
        <template #body="slotProps">
          <span class="inline-flex items-center text-sm text-gray-800">
            {{ slotProps.data.rest_time }}
          </span>
        </template>
      </Column>
      <Column field="workTime" class="px-2 text-center text-sm">
        <template #header>
          <div class="flex flex-col items-center text-center">
            <div>作業時間</div>
            <div class="text-xs">
              {{ selectedCreator.includes_rest ? '(休憩含む)' : '(休憩除く)' }}
            </div>
          </div>
        </template>
        <template #body="slotProps">
          <span class="inline-flex items-center text-sm text-gray-800">
            {{ slotProps.data.actual_work_time }}
          </span>
        </template>
      </Column>
      <Column field="group_key_price" class="px-2 text-center text-sm">
        <template #header>
          <div class="flex-1 flex-col items-center text-center">
            <div>金額</div>
            <div class="text-xs">
              {{ selectedCreator.tax_included ? '(税込)' : '(税抜)' }}
            </div>
          </div>
        </template>
        <template #body="slotProps">
          <div class="inline-flex items-center text-sm">
            <template v-if="slotProps.data.grouped_actual_detail">
              {{
                Number(
                  slotProps.data.grouped_actual_detail.price
                ).toLocaleString()
              }}
            </template>
            <template v-else>
              <span class="text-sm text-gray-800">
                {{
                  Number(
                    slotProps.data.modified_actual_details.price
                  ).toLocaleString()
                }}
              </span>
            </template>
          </div>
        </template>
      </Column>
      <Column field="group_key_tax" header="税率" class="px-2 text-sm">
        <template #body="slotProps">
          <template v-if="slotProps.data.grouped_actual_detail">
            {{ slotProps.data.grouped_actual_detail.tax }}%
          </template>
          <template v-else>
            <span class="inline-flex items-center text-sm text-gray-800">
              <span class="text-sm text-gray-800">
                {{
                  slotProps.data.modified_actual_details.tax === null
                    ? ''
                    : `${slotProps.data.modified_actual_details.tax}%`
                }}</span
              >
            </span>
          </template>
        </template>
      </Column>
    </DataTable>
  </div>
  <div
    v-show="layoutData.isDisplayTotal && !data.isEditMode"
    class="flex items-start justify-end"
  >
    <table class="mx-2 text-sm">
      <tbody class="border">
        <tr class="bg-white">
          <td class="border bg-gray-100 px-6 py-3">実績合計</td>
          <td class="border px-6 py-3">{{ data.totalPrices.work_time }}</td>
        </tr>
        <tr class="bg-white">
          <td class="border bg-gray-100 px-6 py-3">キャンセル料</td>
          <td class="border px-6 py-3">
            <template v-if="data.totalPrices.each_tax">
              ¥{{
                Number(data.totalPrices.each_tax['0'].sub_price).toLocaleString()
              }}
            </template>
          </td>
        </tr>
        <tr class="bg-white">
          <td class="border bg-gray-100 px-6 py-3">金額合計（税抜）</td>
          <td class="border px-6 py-3">
            ¥{{ Number(data.totalPrices.sub_price).toLocaleString() }}
          </td>
        </tr>
        <tr class="bg-white">
          <td class="border bg-gray-100 px-6 py-3">金額合計（税込）</td>
          <td class="border px-6 py-3">
            ¥{{ Number(data.totalPrices.total_price).toLocaleString() }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-checkbox-box) {
  border: 1px solid #cbd5e1;
  width: 1.25rem;
  height: 1.25rem;
}

:deep(.p-checkbox) {
  width: 1.25rem;
  height: 1.25rem;
}
</style>
